import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  InputAdornment,
  IconButton,
  CircularProgress,
} from '@mui/material';
import { Visibility, VisibilityOff, EmailOutlined, LockOutlined } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const LOGIN_ERROR_DISPLAY_MS = 10000;

const LoginForm = () => {
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState('');
  const [displayError, setDisplayError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const { login, loading } = useAuth();
  const hideErrorTimerRef = useRef(null);

  const showTopError = useCallback((message) => {
    const text = String(message || '').trim();
    if (!text) return;

    setDisplayError(text);
    if (hideErrorTimerRef.current) {
      clearTimeout(hideErrorTimerRef.current);
    }
    hideErrorTimerRef.current = setTimeout(() => {
      setDisplayError('');
      setFormError('');
      hideErrorTimerRef.current = null;
    }, LOGIN_ERROR_DISPLAY_MS);
  }, []);

  useEffect(() => {
    if (formError) {
      showTopError(formError);
    }
  }, [formError, showTopError]);

  useEffect(() => () => {
    if (hideErrorTimerRef.current) {
      clearTimeout(hideErrorTimerRef.current);
    }
  }, []);

  const handleChange = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    if (!formData.password) {
      newErrors.password = 'Password is required';
    }
    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      setFormError(Object.values(newErrors).join(' '));
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      return;
    }
    const result = await login(formData);
    if (result?.success) {
      if (hideErrorTimerRef.current) {
        clearTimeout(hideErrorTimerRef.current);
      }
      setDisplayError('');
      setFormError('');
      return;
    }
    if (result?.message) {
      setFormError(result.message);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate>
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
        <Box component="img" src="/logo.png" alt="Luxe" sx={{ height: 64 }} />
      </Box>
      <Typography variant="h5" fontWeight={700} color="primary.main" gutterBottom align="center">
        Welcome Back
      </Typography>
      <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: displayError ? 2 : 4 }}>
        Sign in to your Luxe Employee account
      </Typography>

      {displayError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {displayError}
        </Alert>
      )}

      <TextField
        fullWidth
        label="Email Address"
        type="email"
        value={formData.email}
        onChange={handleChange('email')}
        error={!!errors.email}
        helperText={errors.email}
        required
        sx={{ mb: 2 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <EmailOutlined sx={{ color: 'text.secondary' }} />
            </InputAdornment>
          ),
        }}
      />

      <TextField
        fullWidth
        label="Password"
        type={showPassword ? 'text' : 'password'}
        value={formData.password}
        onChange={handleChange('password')}
        error={!!errors.password}
        helperText={errors.password}
        required
        sx={{ mb: 1 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <LockOutlined sx={{ color: 'text.secondary' }} />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <Box sx={{ textAlign: 'right', mb: 2 }}>
        <Typography
          component={RouterLink}
          to="/forgot-password"
          variant="body2"
          sx={{ color: 'secondary.dark', fontWeight: 600, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
        >
          Forgot password?
        </Typography>
      </Box>

      <Button
        type="submit"
        fullWidth
        variant="contained"
        disabled={loading}
        size="large"
        sx={{
          background: 'linear-gradient(135deg, #7B6E4B 0%, #A89770 100%)',
          color: '#1A1A2E',
          fontWeight: 700,
          py: 1.5,
          mb: 2,
          '&:hover': { background: 'linear-gradient(135deg, #5B5235 0%, #7B6E4B 100%)' },
        }}
      >
        {loading ? <CircularProgress size={24} color="inherit" /> : 'Sign In'}
      </Button>
    </Box>
  );
};

export default LoginForm;
